import { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = "/api";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('🚀 Sending:', formData);
      const response = await axios.post(`${API}/contact`, formData, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000  // 10s timeout
      });
      console.log('✅ Success:', response.data);
      toast.success('Message sent! We\'ll reply within 24 hours.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('❌ Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      const errorMsg = error.response?.data?.error || 
                      `Error ${error.response?.status || error.code}: ${error.message}`;
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-5xl md:text-7xl text-primary mb-6 tracking-tight">Get In Touch</h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Have questions about solar energy? We're here to help. Reach out to us and let's start your solar journey.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info - Nagpur Realistic */}
            <div>
              <h2 className="font-heading font-semibold text-3xl md:text-4xl text-primary mb-8">Contact Information</h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-2xl p-4">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-xl mb-2">Visit Us</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      9, Sadbhavna Nagar,<br />
                      Zingabai Takli, Nagpur,<br />
                      Maharashtra 440024.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-2xl p-4">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-xl mb-2">Call Us</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      <a href="tel:+917588799488" className="hover:text-primary transition-colors">+91 7588799488</a><br />
                      <a href="tel:+919404977042" className="hover:text-primary transition-colors">+91 9404977042</a><br />
                      <a href="tel:+918237683132" className="hover:text-primary transition-colors">+91 8237683132</a><br />
                      Mon-Sat: 9AM - 7PM IST
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-2xl p-4">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-xl mb-2">Email Us</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      <a href="mailto:info@solarvisiontech.in" className="hover:text-primary transition-colors">info@solarvisiontech.in</a><br />
                      <a href="mailto:sales@solarvisiontech.in" className="hover:text-primary transition-colors">sales@solarvisiontech.in</a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Nagpur Map */}
              <div className="mt-12 w-full h-[350px] md:h-[450px] rounded-2xl overflow-hidden shadow-2xl border border-border/50">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d60000.5!2d79.0882!3d21.1458!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c3197b2c4d1d%3A0xbec4d4a7b8f3c2d1!2sSitabuldi%2C%20Nagpur!5e0!3m2!1sen!2sin!4v1703123456789!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{border:0}}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-card border border-border/50 shadow-xl rounded-3xl p-8 md:p-12 sticky top-24">
                <h2 className="font-heading font-semibold text-3xl text-primary mb-8">Send Message</h2>
                
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2 text-foreground">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-input-name"
                        className="w-full bg-muted/30 border border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl h-12 px-4 transition-all focus:outline-none placeholder:text-muted-foreground"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2 text-foreground">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-input-email"
                        className="w-full bg-muted/30 border border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl h-12 px-4 transition-all focus:outline-none placeholder:text-muted-foreground"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2 text-foreground">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="contact-input-phone"
                        className="w-full bg-muted/30 border border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl h-12 px-4 transition-all focus:outline-none placeholder:text-muted-foreground"
                        placeholder="Enter your phone number"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2 text-foreground">
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows="6"
                        value={formData.message}
                        onChange={handleChange}
                        data-testid="contact-input-message"
                        className="w-full bg-muted/30 border border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 transition-all focus:outline-none resize-vertical placeholder:text-muted-foreground"
                        placeholder="Tell us about your solar project needs..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-button"
                      className="w-full bg-gradient-to-r from-primary to-secondary text-white hover:from-primary/90 hover:to-secondary/90 disabled:from-primary/50 disabled:to-secondary/50 rounded-full px-8 py-6 text-lg font-bold transition-all hover:scale-[1.02] shadow-xl disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    >
                      {loading ? (
                        <>
                          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
